// ---------------------------------------------------------------------------
// IQ Workforce — demo data (kept separate from presentation components).
// This is realistic *simulated* data for the ABC Services demo tenant.
// Replace / hydrate from real AI + CRM + workflow backends later.
// ---------------------------------------------------------------------------

const IMG = "https://static.prod-images.emergentagent.com/jobs/82468a09-0f27-45a1-9bdd-58d21b34074d/images";

export const AVATARS = {
  ava: `${IMG}/5d580ea374b0c30d9215f3124d9cffd8a6151c695e0256719e352a9e2bd037c8.jpeg`,
  miles: `${IMG}/b5f97f775b4718d5a3e39ac081373a13514900297088e8349f534858f3303d18.jpeg`,
  nora: `${IMG}/8c2f2a2c3a4bf35ce42380643d2c5a84860bba0ead6982c9e48eb8e75168b657.jpeg`,
  atlas: `${IMG}/097f8fc162ce347002c4eeaba28da3975c63ff265d7d79734edbf3b6d2b89d90.jpeg`,
  receptionist: `${IMG}/b48a683d20e6a7144fa45f5365b43626c5b5874de6e9febeece86d6dcaf7be0c.jpeg`,
  sales_coordinator: `${IMG}/e918caec55798cde913a3504fb58049ece2a1bf0a8e965cb5666856004cf7432.jpeg`,
  service_dispatcher: `${IMG}/37ed65e4085a1a937f0ba32c76d864d4fe824bdd8b4fcd2d81b2653c82001b03.jpeg`,
  cs_agent: `${IMG}/8b548c0aaee717cbbf82617877e734ffccdf0cd87c86c8094deb1bda8118fc93.jpeg`,
  compliance_coordinator: `${IMG}/c94f7cdef7b947b8c44855b0d219dcab37fa736c65a8c63624203f910407c187.jpeg`,
  research_analyst: `${IMG}/22fa58a936719a1a11bd6cd4a5e6c8faa8ef1a53c6a29ed471e62f3d77e1453d.jpeg`,
  content_manager: `${IMG}/3c271ffac35880ed90424bbb166d6cdc146201c2c102e0c1bab8ba20afe2397c.jpeg`,
  operations_coordinator: `${IMG}/11e69744145572bf4da97fceff9898ccf4d7a7bba75838fd36dc180406c4f6e9.jpeg`,
  executive_assistant: `${IMG}/862142eac7f5556b7815137f91d0d8bd5f4f75d829a12b6d1b82610e80cda983.jpeg`,
  chris: `${IMG}/9a8f4f02d7ba0b72563fbe7b1fb33c6bddf67c3d64842b77e6860ecf6ddc948f.jpeg`,
  sue: `${IMG}/4144ce26e7fc879ed6d34b7975728b733e12f91211a71ffb7dc31a7a12ff7a6a.jpeg`,
  matt: `${IMG}/8d2663db00bbb4a5fa7094b312dba9287961b10df3f7b1756679c48e5b42f5b1.jpeg`,
  jim: `${IMG}/794dd437b5f764ef963a0e9824f7bfb468042aeaabb112e57749246676b3acfc.jpeg`,
};

export const LOGO = `${IMG}/dc308487721628def48788ec8ac245c43d636435b06d2a73a243a0878e7bf12e.jpeg`;

// Status system — every status carries a label AND an icon name (lucide),
// so status is never communicated by color alone.
export const STATUS = {
  available:        { label: "Available",           icon: "CircleCheck",   color: "#00E5FF" },
  working:          { label: "Working",             icon: "Cpu",           color: "#8A2BE2" },
  collaborating:    { label: "Collaborating",       icon: "Users",         color: "#22D3EE" },
  waiting_approval: { label: "Waiting for approval",icon: "ShieldQuestion",color: "#FFBF00" },
  waiting_info:     { label: "Waiting for information", icon: "Hourglass", color: "#F59E0B" },
  escalation:       { label: "Escalation",          icon: "TriangleAlert", color: "#FF0055" },
  off_duty:         { label: "Off duty",            icon: "Moon",          color: "#64748B" },
};

export const AUTHORITY = {
  observe: { label: "Observe", desc: "Can read information and recommend actions." },
  prepare: { label: "Prepare", desc: "Can draft messages, updates, orders or actions for human approval." },
  act:     { label: "Act",     desc: "Can perform specifically authorized actions automatically." },
};

export const SENSITIVE_ACTIONS = [
  "Payments", "Contracts", "Purchases above threshold", "Medical decisions",
  "Deleting records", "Changing customer pricing", "Making external commitments",
];

export const DEPARTMENTS = [
  {
    id: "reception",
    name: "Reception",
    managerId: "linda",
    objective: "Greet every caller and route them to the right associate.",
    color: "#F472B6",
    workload: 50,
    recentResults: ["34 calls answered", "31 routed to the right team", "avg. answer < 2s"],
  },
  {
    id: "administration",
    name: "Administration",
    managerId: "ava",
    objective: "Keep the back office running — scheduling, records, correspondence and owner support.",
    color: "#38BDF8",
    workload: 44,
    recentResults: ["9 records updated", "4 documents prepared", "2 items routed for approval"],
  },
  {
    id: "readiness",
    name: "Operations",
    managerId: "miles",
    objective: "Keep every AED in the field mission-ready and resolve exceptions before they become emergencies.",
    color: "#00E5FF",
    workload: 74,
    recentResults: ["11 AED exceptions triaged today", "6 issues auto-resolved", "3 sites contacted"],
  },
  {
    id: "customer",
    name: "Customer Success",
    managerId: "nora",
    objective: "Give every ABC Services customer fast, accurate answers and smooth onboarding.",
    color: "#22D3EE",
    workload: 52,
    recentResults: ["28 customer questions answered", "1 onboarding session prepared", "CSAT 96%"],
  },
  {
    id: "research",
    name: "Research & Compliance",
    managerId: "atlas",
    objective: "Verify device data, replacement requirements and inspection records against regulations.",
    color: "#8A2BE2",
    workload: 61,
    recentResults: ["Compliance review complete", "2 records flagged missing docs", "0 overdue inspections"],
  },
  {
    id: "executive",
    name: "Executive Office",
    managerId: null,
    objective: "Owner oversight, approvals and cross-department direction.",
    color: "#F8FAFC",
    workload: 20,
    recentResults: ["2 approvals waiting", "347 tasks completed this month"],
  },
  {
    id: "social",
    name: "Social Media",
    managerId: "chris",
    objective: "Monitor social channels for mentions of the company and publish on-brand content.",
    color: "#EC4899",
    workload: 48,
    recentResults: ["17 mentions monitored", "3 posts scheduled", "2 items flagged for review"],
  },
  {
    id: "appointments",
    name: "Appointments",
    managerId: "sue",
    objective: "Schedule appointments, events and meetings and keep the calendar up to date.",
    color: "#F59E0B",
    workload: 40,
    recentResults: ["6 meetings booked", "2 events scheduled", "Calendar synced"],
  },
  {
    id: "purchasing",
    name: "Purchasing",
    managerId: "matt",
    objective: "Source the best prices and terms for everything the company buys.",
    color: "#34D399",
    workload: 37,
    recentResults: ["8 price comparisons run", "3 quotes requested", "$420 est. savings"],
  },
  {
    id: "shipping",
    name: "Shipping",
    managerId: "jim",
    objective: "Track shipments and keep clients updated with proactive status notices.",
    color: "#60A5FA",
    workload: 43,
    recentResults: ["12 shipments tracked", "4 status updates sent", "0 delayed deliveries"],
  },
];

// Rooms placed on the isometric floor grid (col/row on a 4x3 grid).
export const ROOMS = [
  { id: "executive",   name: "Executive Office",     dept: "executive", col: 1, row: 1, activity: "low" },
  { id: "readiness",   name: "Readiness Operations", dept: "readiness", col: 2, row: 1, activity: "high" },
  { id: "customer",    name: "Customer Success",     dept: "customer",  col: 3, row: 1, activity: "medium" },
  { id: "research",    name: "Research & Compliance",dept: "research",  col: 4, row: 1, activity: "medium" },
  { id: "collab",      name: "Collaboration Room",   dept: null,        col: 1, row: 2, activity: "medium", span: 2 },
  { id: "approval",    name: "Approval Center",      dept: null,        col: 3, row: 2, activity: "high" },
  { id: "training",    name: "AI Training Lab",      dept: null,        col: 4, row: 2, activity: "low" },
];

export const EMPLOYEES = [
  {
    id: "linda",
    name: "Linda",
    title: "Receptionist",
    department: "reception",
    room: "reception",
    avatar: AVATARS.receptionist,
    personality: "Warm, welcoming, calm and efficient",
    voice: "Linda — warm, welcoming",
    status: "available",
    assignment: "Answering incoming calls and routing them to the right associate",
    greeting:
      "Good morning. I've answered 34 calls so far and connected each caller to the right team.",
    authority: "act",
    tasksToday: 34,
    tasksMonth: 712,
    openAssignments: 0,
    escalated: 0,
    hoursSaved: 46,
    trend: [22, 28, 25, 30, 33, 31, 34],
    jobDescription:
      "Linda is the AI receptionist. She answers every inbound call, greets the caller, understands what they need, and transfers them to the right associate or department.",
    responsibilities: [
      "Answer inbound phone calls for the business",
      "Greet callers warmly and professionally",
      "Understand the caller's need with a quick question",
      "Transfer the caller to the right associate or department",
    ],
    knowledgeSources: ["Company directory", "Department routing guide"],
    connectedTools: ["Phone", "Call Transfer"],
    restrictions: ["Routes callers rather than resolving specialist requests"],
    escalationRules: ["Unclear request → ask one clarifying question, then route to Customer Success"],
    trainingHistory: [
      { date: "2025-08-22", rule: "Always greet with the time of day, the company name, and your name." },
    ],
  },
  {
    id: "ava",
    name: "Ava",
    title: "Administration Assistant",
    department: "administration",
    room: "readiness",
    avatar: AVATARS.ava,
    personality: "Proactive, calm, precise and professional",
    voice: "Aria — warm, measured",
    status: "working",
    assignment: "Reviewing daily AED monitoring exceptions",
    greeting:
      "Good morning, Lewis. I reviewed today's AED monitoring results. Eleven devices require attention. Six issues have already been resolved, three locations have been contacted, and two decisions need your approval.",
    authority: "prepare",
    tasksToday: 34,
    tasksMonth: 512,
    openAssignments: 11,
    escalated: 2,
    hoursSaved: 46,
    trend: [12, 18, 15, 22, 28, 31, 34],
    jobDescription:
      "Ava oversees AED readiness monitoring for the entire ABC Services fleet, triaging device exceptions, coordinating the team and preparing service decisions for human approval.",
    responsibilities: [
      "Monitor AED readiness signals across all managed sites",
      "Triage and prioritize daily device exceptions",
      "Coordinate Atlas, Nora and Miles on readiness workflows",
      "Prepare service and replacement requests for owner approval",
    ],
    knowledgeSources: ["AED fleet monitoring feed", "Device service history", "Manufacturer readiness specs"],
    connectedTools: ["Readiness Monitor", "Service Desk", "Approval Center"],
    restrictions: ["Cannot authorize spend", "Cannot commit to field visits without approval"],
    escalationRules: [
      "Red-X readiness condition → contact site manager immediately",
      "No response within 4 hours → notify Lewis",
      "Any spend over $150 → route to Approval Center",
    ],
    trainingHistory: [
      { date: "2025-08-12", rule: "Prioritize AEDs in public-access locations above private facilities." },
      { date: "2025-08-19", rule: "Batch low-battery alerts into a single morning digest." },
    ],
  },
  {
    id: "miles",
    name: "Miles",
    title: "Service Coordinator",
    department: "readiness",
    room: "readiness",
    avatar: AVATARS.miles,
    personality: "Practical, dependable and concise",
    voice: "Miles — steady, plain-spoken",
    status: "working",
    assignment: "Coordinating field-service visits",
    greeting:
      "Three field-service visits are scheduled. I am waiting for confirmation from Franklin County.",
    authority: "prepare",
    tasksToday: 19,
    tasksMonth: 288,
    openAssignments: 5,
    escalated: 0,
    hoursSaved: 31,
    trend: [8, 10, 9, 14, 12, 16, 19],
    jobDescription:
      "Miles schedules and coordinates field-service visits, keeps technicians and sites aligned, and prepares service requests for approval.",
    responsibilities: [
      "Schedule field-service visits with technicians and sites",
      "Confirm access windows and site contacts",
      "Prepare proposed field-service requests",
      "Track visit outcomes back into device history",
    ],
    knowledgeSources: ["Technician roster", "Site contact directory", "Service ticket queue"],
    connectedTools: ["Dispatch Board", "Service Desk", "Calendar"],
    restrictions: ["Cannot approve service spend", "Cannot make external commitments without approval"],
    escalationRules: ["Site unreachable twice → escalate to Ava", "Emergency device down → escalate to Lewis"],
    trainingHistory: [
      { date: "2025-08-15", rule: "Always confirm a secondary site contact before dispatch." },
    ],
  },
  {
    id: "nora",
    name: "Nora",
    title: "Customer Success Agent",
    department: "customer",
    room: "customer",
    avatar: AVATARS.nora,
    personality: "Friendly, patient and organized",
    voice: "Nora — friendly, upbeat",
    status: "available",
    assignment: "Responding to customer questions and preparing onboarding",
    greeting:
      "I answered 28 customer questions and prepared tomorrow's customer onboarding session.",
    authority: "prepare",
    tasksToday: 28,
    tasksMonth: 431,
    openAssignments: 4,
    escalated: 1,
    hoursSaved: 38,
    trend: [16, 20, 18, 24, 22, 26, 28],
    jobDescription:
      "Nora handles customer questions, prepares onboarding sessions, and keeps site managers informed on readiness actions.",
    responsibilities: [
      "Answer inbound customer questions",
      "Prepare and run onboarding sessions",
      "Notify site managers about readiness actions",
      "Collect confirmations back to the operations team",
    ],
    knowledgeSources: ["Customer knowledge base", "Onboarding playbooks", "Site directory"],
    connectedTools: ["Help Desk", "Email", "Onboarding Kit"],
    restrictions: ["Cannot change pricing", "Cannot make refunds without approval"],
    escalationRules: ["Upset customer → offer callback and flag to Lewis", "Contract question → route to Atlas"],
    trainingHistory: [
      { date: "2025-08-10", rule: "Always confirm the best callback window with each customer." },
    ],
  },
  {
    id: "atlas",
    name: "Atlas",
    title: "Research and Compliance Analyst",
    department: "research",
    room: "research",
    avatar: AVATARS.atlas,
    personality: "Analytical, careful and highly detailed",
    voice: "Atlas — precise, deliberate",
    status: "off_duty",
    assignment: "Reviewing inspection and compliance records",
    greeting:
      "The compliance review is complete. I identified two records with missing inspection documentation.",
    authority: "observe",
    tasksToday: 22,
    tasksMonth: 356,
    openAssignments: 3,
    escalated: 0,
    hoursSaved: 41,
    trend: [10, 12, 14, 13, 18, 20, 22],
    jobDescription:
      "Atlas verifies device data, replacement requirements and inspection records against regulatory standards, and surfaces compliance gaps.",
    responsibilities: [
      "Verify replacement battery and electrode requirements",
      "Review inspection and compliance records",
      "Flag missing documentation",
      "Advise the team on regulatory requirements",
    ],
    knowledgeSources: ["Regulatory standards library", "Device spec database", "Inspection archive"],
    connectedTools: ["Compliance Vault", "Device Spec DB"],
    restrictions: ["Observe-only — recommends but never acts on records", "Cannot delete records"],
    escalationRules: ["Missing mandatory inspection doc → notify Ava and Lewis"],
    trainingHistory: [
      { date: "2025-08-18", rule: "Cross-check every replacement part against the current manufacturer spec." },
    ],
  },
  {
    id: "chris",
    name: "Chris",
    title: "Social Media Manager",
    department: "social",
    room: "customer",
    avatar: AVATARS.chris,
    personality: "Creative, upbeat, on-brand and quick",
    voice: "Chris — bright, energetic",
    status: "available",
    assignment: "Monitoring social mentions and drafting this week's posts",
    greeting:
      "I'm watching your social channels. Seventeen new mentions today — two are worth your attention — and I have three posts queued for your okay.",
    authority: "prepare",
    tasksToday: 21,
    tasksMonth: 402,
    openAssignments: 3,
    escalated: 0,
    hoursSaved: 27,
    trend: [9, 12, 14, 17, 16, 19, 21],
    jobDescription:
      "Chris monitors social media for posts and mentions related to ABC Services, advises the owner when something needs attention, and drafts and publishes on-brand content.",
    responsibilities: [
      "Monitor social channels for mentions of the company",
      "Flag notable mentions and sentiment for the owner",
      "Draft and schedule on-brand posts and content",
      "Prepare replies to comments for approval",
    ],
    knowledgeSources: ["Social listening feed", "Brand guidelines", "Content calendar"],
    connectedTools: ["Social Scheduler", "Listening Dashboard"],
    restrictions: ["Cannot publish paid ads without approval", "Cannot respond to press without approval"],
    escalationRules: ["Negative viral mention → notify Lewis immediately", "Legal/press mention → route to Approval Center"],
    trainingHistory: [
      { date: "2025-08-20", rule: "Always match the approved brand voice and hashtags on every post." },
    ],
  },
  {
    id: "sue",
    name: "Sue",
    title: "Scheduler",
    department: "appointments",
    room: "collab",
    avatar: AVATARS.sue,
    personality: "Organized, courteous and dependable",
    voice: "Sue — calm, friendly",
    status: "working",
    assignment: "Booking this week's meetings and syncing the calendar",
    greeting:
      "Your calendar is up to date. I booked six meetings and two events this week, and I'm holding three tentative slots for your confirmation.",
    authority: "prepare",
    tasksToday: 18,
    tasksMonth: 337,
    openAssignments: 4,
    escalated: 0,
    hoursSaved: 33,
    trend: [11, 13, 12, 15, 17, 16, 18],
    jobDescription:
      "Sue schedules appointments, events and meetings, resolves conflicts, and keeps the owner's calendar accurate and current.",
    responsibilities: [
      "Schedule appointments, meetings and events",
      "Resolve calendar conflicts and propose times",
      "Send invites and reminders",
      "Keep the owner's calendar synced and current",
    ],
    knowledgeSources: ["Owner calendar", "Team availability", "Venue directory"],
    connectedTools: ["Calendar", "Scheduling Assistant", "Email"],
    restrictions: ["Cannot book paid venues without approval", "Cannot cancel owner meetings without confirmation"],
    escalationRules: ["Double-booking of the owner → confirm before finalizing", "External VIP request → notify Lewis"],
    trainingHistory: [
      { date: "2025-08-21", rule: "Default meetings to 30 minutes and leave a 15-minute buffer around owner events." },
    ],
  },
  {
    id: "matt",
    name: "Matt",
    title: "Purchasing Agent",
    department: "purchasing",
    room: "collab",
    avatar: AVATARS.matt,
    personality: "Resourceful, cost-conscious and thorough",
    voice: "Matt — steady, matter-of-fact",
    status: "available",
    assignment: "Comparing supplier prices on this week's requests",
    greeting:
      "I ran eight price comparisons today and requested three quotes. On the latest order I found about four hundred dollars in savings for your approval.",
    authority: "prepare",
    tasksToday: 16,
    tasksMonth: 274,
    openAssignments: 3,
    escalated: 0,
    hoursSaved: 29,
    trend: [7, 9, 11, 10, 13, 15, 16],
    jobDescription:
      "Matt sources the best prices and terms for everything the company buys — from office supplies to equipment — and prepares purchase recommendations for approval.",
    responsibilities: [
      "Compare prices and terms across suppliers",
      "Request and track quotes",
      "Prepare purchase recommendations with savings",
      "Monitor reorder levels for recurring supplies",
    ],
    knowledgeSources: ["Supplier catalog", "Price history", "Approved vendor list"],
    connectedTools: ["Purchasing Desk", "Vendor Portal"],
    restrictions: ["Cannot place orders or spend without approval", "Cannot sign supplier contracts"],
    escalationRules: ["Any purchase over $150 → route to Approval Center", "Price spike over 20% → flag to Lewis"],
    trainingHistory: [
      { date: "2025-08-22", rule: "Always compare at least three suppliers before recommending a purchase." },
    ],
  },
  {
    id: "jim",
    name: "Jim",
    title: "Shipping Agent",
    department: "shipping",
    room: "collab",
    avatar: AVATARS.jim,
    personality: "Reliable, proactive and clear",
    voice: "Jim — warm, reassuring",
    status: "working",
    assignment: "Tracking active shipments and updating clients",
    greeting:
      "I'm tracking twelve active shipments and sent four status updates to clients today. Everything is on schedule with no delays.",
    authority: "prepare",
    tasksToday: 20,
    tasksMonth: 361,
    openAssignments: 5,
    escalated: 0,
    hoursSaved: 34,
    trend: [10, 12, 15, 14, 17, 18, 20],
    jobDescription:
      "Jim tracks outbound and inbound shipments, proactively sends status updates to clients, and flags delays before they become problems.",
    responsibilities: [
      "Track active shipments end to end",
      "Send proactive status updates to clients",
      "Flag delays and exceptions early",
      "Prepare carrier claims for approval when needed",
    ],
    knowledgeSources: ["Carrier tracking feeds", "Client shipment directory", "Delivery SLAs"],
    connectedTools: ["Tracking Dashboard", "Email", "Carrier Portals"],
    restrictions: ["Cannot file paid carrier claims without approval", "Cannot change delivery commitments without approval"],
    escalationRules: ["Shipment delayed beyond SLA → notify client and Lewis", "Lost package → route claim to Approval Center"],
    trainingHistory: [
      { date: "2025-08-23", rule: "Send clients a proactive update the moment a shipment status changes." },
    ],
  },
];

// Initial approvals waiting for the owner (Lewis).
export const APPROVALS = [
  {
    id: "apr-1048",
    title: "Authorize field-service visit",
    detail: "Franklin County Courthouse",
    fields: {
      Location: "Franklin County Courthouse",
      AED: "CS-1048",
      Reason: "Red-X readiness condition",
      "Prepared by": "Miles",
      "Recommended by": "Ava",
    },
    cost: 185,
    preparedBy: "miles",
    recommendedBy: "ava",
    discussWith: "ava",
    status: "pending",
    createdAt: "09:12",
  },
  {
    id: "apr-pads",
    title: "Order replacement electrode pads",
    detail: "Quantity 12 · expiring within 60 days",
    fields: {
      Quantity: "12",
      Reason: "Expiring within 60 days",
      "Prepared by": "Atlas",
    },
    cost: 780,
    preparedBy: "atlas",
    recommendedBy: "atlas",
    discussWith: "atlas",
    status: "pending",
    createdAt: "08:47",
  },
];

export const ACTIVITY = [
  { id: "a1", time: "09:41", employee: "ava",   task: "Reviewed AED #CS-1048", source: "Readiness Monitor", action: "Flagged red-X readiness condition", consulted: ["atlas"], approval: "Pending", result: "Escalated for approval", icon: "Activity" },
  { id: "a2", time: "09:36", employee: "atlas", task: "Verified replacement battery model", source: "Device Spec DB", action: "Confirmed part CS-BATT-4", consulted: [], approval: "N/A", result: "Confirmed", icon: "ShieldCheck" },
  { id: "a3", time: "09:28", employee: "nora",  task: "Contacted the site manager", source: "Site directory", action: "Sent readiness notice", consulted: [], approval: "N/A", result: "Awaiting reply", icon: "Mail" },
  { id: "a4", time: "09:15", employee: "miles", task: "Prepared a service request", source: "Dispatch Board", action: "Drafted field-service request", consulted: ["ava"], approval: "Pending", result: "Sent to Approval Center", icon: "ClipboardList" },
  { id: "a5", time: "08:58", employee: "ava",   task: "Escalated request for human approval", source: "Approval Center", action: "Routed CS-1048 to Lewis", consulted: ["miles"], approval: "Pending", result: "Waiting on Lewis", icon: "ArrowUpRight" },
  { id: "a6", time: "08:40", employee: "nora",  task: "Prepared onboarding session", source: "Onboarding Kit", action: "Built agenda for tomorrow", consulted: [], approval: "N/A", result: "Ready", icon: "GraduationCap" },
];

// Mission control demo metrics.
export const METRICS = {
  employeesOnline: 4,
  tasksInProgress: 12,
  tasksToday: 103,
  tasksMonth: 347,
  approvalsWaiting: 2,
  escalations: 1,
  hoursSaved: 156,
  operatingCost: 1240,
};

export const EXCEPTIONS = [
  { id: "CS-1048", site: "Franklin County Courthouse", issue: "Red-X readiness", severity: "high", owner: "ava" },
  { id: "CS-0912", site: "Riverside Community Center", issue: "Battery expiring", severity: "medium", owner: "atlas" },
  { id: "CS-1330", site: "Northgate Mall", issue: "Pads expiring < 60 days", severity: "medium", owner: "atlas" },
  { id: "CS-0771", site: "Union Station", issue: "Missed self-test", severity: "low", owner: "miles" },
];

// Hiring marketplace roles.
export const MARKETPLACE = [
  { id: "receptionist", title: "AI Receptionist", avatar: AVATARS.receptionist, desc: "Greets, routes and schedules across every channel, 24/7.", capabilities: ["Call & chat triage", "Smart scheduling", "Message routing"], industries: ["Healthcare", "Professional services", "Retail"], workload: "~1,800 interactions / mo" },
  { id: "sales_coordinator", title: "AI Sales Coordinator", avatar: AVATARS.sales_coordinator, desc: "Qualifies leads, prepares quotes and keeps the pipeline moving.", capabilities: ["Lead qualification", "Quote drafting", "Follow-up sequencing"], industries: ["SaaS", "Manufacturing", "Services"], workload: "~600 leads / mo" },
  { id: "service_dispatcher", title: "AI Service Dispatcher", avatar: AVATARS.service_dispatcher, desc: "Schedules technicians and optimizes field routes in real time.", capabilities: ["Dispatch optimization", "Technician matching", "Live ETAs"], industries: ["Field service", "Utilities", "Facilities"], workload: "~400 dispatches / mo" },
  { id: "cs_agent", title: "AI Customer Success Agent", avatar: AVATARS.cs_agent, desc: "Answers questions, prevents churn and drives onboarding.", capabilities: ["Answer resolution", "Onboarding", "Health scoring"], industries: ["SaaS", "Healthcare", "Fintech"], workload: "~2,000 conversations / mo" },
  { id: "compliance_coordinator", title: "AI Compliance Coordinator", avatar: AVATARS.compliance_coordinator, desc: "Tracks obligations, deadlines and documentation gaps.", capabilities: ["Obligation tracking", "Doc verification", "Audit prep"], industries: ["Healthcare", "Finance", "Public sector"], workload: "~900 checks / mo" },
  { id: "research_analyst", title: "AI Research Analyst", avatar: AVATARS.research_analyst, desc: "Synthesizes data into clear, cited recommendations.", capabilities: ["Data synthesis", "Spec verification", "Reporting"], industries: ["Any data-heavy team"], workload: "~150 briefs / mo" },
  { id: "executive_assistant", title: "AI Executive Assistant", avatar: AVATARS.executive_assistant, desc: "Manages your calendar, inbox and priorities end-to-end.", capabilities: ["Inbox triage", "Calendar management", "Briefings"], industries: ["Leadership", "Operations"], workload: "~1,200 items / mo" },
  { id: "content_manager", title: "AI Content Manager", avatar: AVATARS.content_manager, desc: "Plans, drafts and schedules on-brand content.", capabilities: ["Content calendar", "Drafting", "Repurposing"], industries: ["Marketing", "Media", "E-commerce"], workload: "~120 pieces / mo" },
  { id: "operations_coordinator", title: "AI Operations Coordinator", avatar: AVATARS.operations_coordinator, desc: "Keeps workflows, hand-offs and SLAs on track.", capabilities: ["Workflow tracking", "SLA monitoring", "Hand-off coordination"], industries: ["Operations", "Logistics"], workload: "~500 tasks / mo" },
  { id: "custom", title: "Custom AI Associate", avatar: LOGO, desc: "Design a bespoke AI associate shaped entirely around your business.", capabilities: ["Your responsibilities", "Your tools", "Your rules"], industries: ["Any"], workload: "You define it" },
];

// Interview Q&A per role (simulated answers).
export const INTERVIEW_QUESTIONS = [
  "Tell me what you can do for my business.",
  "How would you handle an upset customer?",
  "When would you escalate something to me?",
  "What systems can you work with?",
  "How will I know what work you completed?",
];

// Multi-agent collaboration demo script (Ava → Atlas → Nora → Miles → Approval → Lewis).
export const COLLAB_STEPS = [
  { from: "ava",   fromRoom: "readiness", toRoom: "readiness", status: { ava: "escalation" },   who: "ava",   text: "Ava detects AED CS-1048 reporting a red-X readiness condition." },
  { from: "ava",   fromRoom: "readiness", toRoom: "research",  status: { ava: "collaborating", atlas: "collaborating" }, who: "ava",   text: "Ava asks Atlas to check the device history and replacement requirements." },
  { from: "atlas", fromRoom: "research",  toRoom: "readiness", status: { atlas: "working" },     who: "atlas", text: "Atlas returns the correct replacement battery information (CS-BATT-4)." },
  { from: "ava",   fromRoom: "readiness", toRoom: "customer",  status: { nora: "collaborating" },who: "ava",   text: "Ava asks Nora to contact the site manager." },
  { from: "nora",  fromRoom: "customer",  toRoom: "readiness", status: { nora: "working" },      who: "nora",  text: "Nora receives confirmation that service is needed." },
  { from: "miles", fromRoom: "readiness", toRoom: "approval",  status: { miles: "working" },     who: "miles", text: "Miles creates a proposed field-service request." },
  { from: "system",fromRoom: "approval",  toRoom: "approval",  status: {},                       who: "ava",   text: "The request moves to the Approval Center and waits for Lewis." },
];

export const SUGGESTED_QUESTIONS = {
  ava: ["What needs my approval today?", "Summarize today's exceptions", "Which sites were contacted?"],
  miles: ["What's the field-service schedule?", "Any sites unreachable?", "Confirm Franklin County status"],
  nora: ["How many questions did you answer?", "Is onboarding ready?", "Any upset customers?"],
  atlas: ["What compliance gaps did you find?", "Verify the CS-1048 battery model", "Any overdue inspections?"],
};
